import NavComponent from "../lib/nav_component";
import {PaneOptionsTitleComponent, PaneOptionsTitleComponentConfig} from "./pane_options_title_component";
import {isElementVisibleInViewport, isElementDisplayedOnPage} from "../../../helpers/element_in_viewport";
import {polyfillRequestIdleCallback} from "../../../polyfills/request_idle_callback_polyfill";

export const PaneOptionsComponentConfig = {
    baseHtmlClass: 'nav_options',
    selector: '.nav_options',

    datasetConfigKey: 'nav_options__online_app_nav_data',
    identifierDatasetConfigKey: 'identifier',
    openDatasetConfigKey: 'open',
    collapsableDatasetConfigKey: 'collapsable',
};

export class PaneOptionsComponent extends NavComponent {

    constructor(id_or_element, pane) {

        super(id_or_element, PaneOptionsComponentConfig);

        this.setDatasetConfig(PaneOptionsComponentConfig.datasetConfigKey);
        this.identifier = this.getIdentifier();
        this.collapsable = this.getCollapsable();

        this.paneOptionsTitleComponent = getPaneOptionsTitleComponent(this);
        this.optionsCount = this.getOptionsCount();
        this.transitionDuration = this.getTransitionDuration();

        this.pane = pane;

        this.height = null;

        this.renderInitial();

        polyfillRequestIdleCallback();

        // We unlock the height for open components so that all the content is shown (in case the calculated height is incorrect for some reasons).
        // The unlocking of the height needs a delay for unknown reason! Otherwise the absolute height sticks.
        if (this.defaultOpenState()) {
            window.setTimeout(() => {
                window.requestIdleCallback(() => {
                    this.unlockHeightWhenOpen(); // Unlocking the height will set the height to `auto` so all content is shown.
                })
            }, this.transitionDuration);
        }
    }


    // State machine helpers…

    /**
     * Reads a property from the state machine's state.
     * The SM `paneOptionsComponents` is an object with keys that are the identifiers of each pane options component.
     *
     * @param prop
     * @returns {*}
     */
    readStateProp(prop) {
        const state = this.readStateMachineValue('paneOptionsComponents')?.[this.identifier];
        return state?.[prop];
    }

    // Rendering…

    renderInitial() {
        this.conditionallySetHtmlClassState(this.collapsable, 'collapsable');
        this.element.style['transitionDuration'] = `${this.transitionDuration}ms`;
    }

    renderHeight() {
        if (!this.collapsable) return;
        this.element.style.height = (this.height == null) ? null : `${this.height}px`;
    }

    render() {
        // Only recompute the height while the element is actually visible. When the nav is
        // detached (`.nav__panes { display: none }` during a close→reopen), every `offsetHeight`
        // reads 0 → `getHeight()` returns null → `renderHeight()` sets `height: auto`, which
        // unclamps a collapsed section so it looks open even though the SM still says closed.
        // Preserving the last good height (the section content can't change while hidden) avoids
        // that corruption; the open/closed classes below still render from SM state, so they stay
        // correct regardless.
        if (this.isVisible()) {
            this.height = this.getHeight();
            this.renderHeight();
        }
        this.conditionallySetHtmlClassState(this.isOpen(), 'open');
        this.conditionallySetHtmlClassState(this.isClosed(), 'closed');
    }

    isVisible() {
        return isElementDisplayedOnPage(this.element);
    }


    // Toggle handling…

    toggleCollapse() {
        this.isOpen() ? this.toggleToClosed() : this.toggleToOpen();
    }

    toggleToClosed() {
        this.lockHeightWhenOpen(); // Before closing we lock the height to an absolute value (it might have been unlocked to `auto`) so that height animations will transition.
        this.dispatchAction('paneOptionsComponentClose', this);
    }

    toggleToOpen() {
        this.dispatchAction('paneOptionsComponentOpen', this);
        // After the rendering and animations have completed we unlock the height (just in case our height calc is wrong) and ensure the contents are in the view.
        window.setTimeout(() => {
            window.requestIdleCallback(() => {
                this.unlockHeightWhenOpen(); // Unlocking the height will set the height to `auto` so all content is shown.
                if (this.isOpen()) this.scrollContentsIntoView();
            })
        }, this.transitionDuration + 100);
    }


    // Height Calcs…

    getHeight() {
        return this.isOpen() ? this.getOpenHeight() : this.getClosedHeight();
    }

    getOpenHeight() {
        let height = this.getContentsHeight();
        if (height === 0) height = null;
        return height;
    }

    getClosedHeight() {
        let height = this.getTitleHeight();
        if (height === 0) height = null;
        return height;
    }

    getTitleHeight() {
        return (!!this.paneOptionsTitleComponent?.element) ? this.paneOptionsTitleComponent.element.offsetHeight : 0;
    }

    getContentsHeight() {
        let height = 0;
        for (let child of this.element.children) {
            height += child.offsetHeight;
        }
        return height;
    }


    // Height locking…

    // Remove any absolutely calculated height and set the element's height to 'auto'.
    unlockHeightWhenOpen() {
        if (this.isClosed()) return;
        this.height = null;
        this.renderHeight();
    }

    lockHeightWhenOpen() {
        if (this.isClosed()) return;
        this.height = this.getOpenHeight();
        this.renderHeight();
    }


    // Attribute helpers…

    isOpen() {
        return this.readStateProp('open') ?? this.defaultOpenState();
    }

    isClosed() {
        return !this.isOpen();
    }

    defaultOpenState() {
        return this.getDatasetConfigValue(PaneOptionsComponentConfig.openDatasetConfigKey) ?? true;
    }

    // Initialization helpers…

    getIdentifier() {
        const identifier = this.getDatasetConfigValue(PaneOptionsComponentConfig.identifierDatasetConfigKey);
        if (!identifier) return;
        return identifier;
    }

    getCollapsable() {
        const collapsable = this.getDatasetConfigValue(PaneOptionsComponentConfig.collapsableDatasetConfigKey);
        return collapsable ?? false;
    }

    getOptionsCount() {
        return this.element.querySelectorAll('.nav_option').length;
    }

    getTransitionDuration() {
        const duration = this.optionsCount * 50;
        return Math.max(200, Math.min(600, duration)); // between 200…600 milliseconds
    }


    // Utils…

    scrollContentsIntoView() {
        const scrollToElement = this.element.querySelectorAll('.nav_option')[1]; // Find the second nav option so that it scrolls a decent amount.
        if (scrollToElement && !isElementVisibleInViewport(scrollToElement)) {
            scrollToElement.scrollIntoView({block: 'end', inline: 'nearest', behavior: 'smooth'});
        }
    }

}

function getPaneOptionsTitleComponent(paneOptionsComponent) {
    const element = paneOptionsComponent.element.querySelector(PaneOptionsTitleComponentConfig.selector);
    if (!element) return;
    return new PaneOptionsTitleComponent(element, paneOptionsComponent);
}
